import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Monitor, Moon, Sun } from "lucide-react";
import { useTheme, type Mode } from "./ThemeProvider";

type Option = { mode: Mode; label: string; Icon: typeof Sun };

const OPTIONS: Option[] = [
  { mode: "light", label: "Light", Icon: Sun },
  { mode: "dark", label: "Dark", Icon: Moon },
  { mode: "system", label: "System", Icon: Monitor },
];

export interface ThemeToggleProps {
  /** Compact mode (icon-only trigger, dropdown popouts to the right). For collapsed sidebars. */
  compact?: boolean;
}

export function ThemeToggle({ compact = false }: ThemeToggleProps = {}) {
  const { mode, setMode } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const current = OPTIONS.find((o) => o.mode === mode) ?? OPTIONS[2];

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const triggerClass = compact
    ? "flex w-full items-center justify-center text-sm text-sidebar-muted hover:text-sidebar-foreground"
    : "flex w-full items-center gap-2 text-sm text-sidebar-muted hover:text-sidebar-foreground";

  const dropdownClass = compact
    ? "absolute bottom-0 left-full z-50 ml-1 w-36 rounded-md border border-sidebar-hover bg-sidebar py-1 text-sidebar-foreground shadow-lg"
    : "absolute bottom-full left-0 z-50 mb-1 w-36 rounded-md border border-sidebar-hover bg-sidebar py-1 text-sidebar-foreground shadow-lg";

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={triggerClass}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={compact ? `Theme: ${current.label}` : undefined}
        title={`Theme: ${current.label}`}
      >
        <current.Icon size={14} aria-hidden="true" />
        <span className={compact ? "sr-only" : undefined}>{current.label}</span>
        {!compact && (
          <ChevronDown
            size={12}
            aria-hidden="true"
            className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {open && (
        <ul role="listbox" className={dropdownClass}>
          {OPTIONS.map(({ mode: m, label, Icon }) => (
            <li key={m} role="option" aria-selected={m === mode}>
              <button
                type="button"
                onClick={() => {
                  setMode(m);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-sidebar-hover ${
                  m === mode ? "text-sidebar-foreground" : "text-sidebar-muted"
                }`}
              >
                <Icon size={14} aria-hidden="true" />
                <span className="flex-1">{label}</span>
                {m === mode && <Check size={12} aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
